import type { Theme} from '@mui/material';

import { useRef, useEffect } from 'react';
import { varAlpha } from 'minimal-shared/utils';

import { Box, Modal, Stack, Button, Typography } from '@mui/material';

import { AiIcon } from '../../../assets/icons';
import { Iconify } from '../../../components/iconify';
import { encodeHTML, decodeHTML } from '../../../utils';
import { Editor } from '../../../components/editor/editor';

import type { AiAssistantModalProps } from './types';
import type { ModalProps } from '../../../store/modals/types';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: {
    xs: '90%',
    sm: '450px',
    md: 'calc(90vw - 200px)',
  },
  maxWidth: '90vw',
  maxHeight: '90vh',
  overflow: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  borderRadius: 2,
};

const editorStyle = {
  maxHeight: 500,
  overflow: 'auto',
  width: '100%',
  backgroundColor: 'background.paper',
};

const editorContainerStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  p: 1.5,
  borderRadius: 2,
};

const AiAssistantModal: React.FC<ModalProps & AiAssistantModalProps> = ({
  visible,
  onClose,
  params,
  getAiEnhancement,
  newContent,
  loading
}) => {
  const { oldContentHtml, jobId, oldContentText, onApply } = params || {};
  const editorRef = useRef<any>(null);

  useEffect(() => {
    getAiEnhancement(jobId, encodeHTML(oldContentHtml));
  }, [jobId, oldContentHtml]);

  const handleApply = () => {
    const html = editorRef.current?.editor?.getHTML();
    const plain = editorRef.current?.editor?.getText();

    onApply(html, plain);
    onClose();
  }

  return (
    <Modal
      open={visible}
      onClose={onClose}
      aria-labelledby="ai-assistant-modal"
      sx={{
        '& .MuiModal-paper': {
          maxWidth: '90vw',
          maxHeight: '90vh',
        },
      }}
    >
      <Box sx={style}>
        <Stack direction="column" spacing={2}>
          <Typography variant="h6" gutterBottom>
            Review Job Description
          </Typography>
          <Stack direction="row" spacing={2}>
            <Box
              sx={{
                ...editorContainerStyle,
                backgroundColor: (theme: Theme) =>
                  varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
              }}
            >
              <Typography variant="h6" gutterBottom>
                Current
              </Typography>
              <Editor value={oldContentHtml} sx={editorStyle} hideToolbar editable={false} />
            </Box>
            <Box sx={{ ...editorContainerStyle, backgroundColor: (theme: Theme) => varAlpha(theme.vars.palette.primary['mainChannel'], 0.08), position: 'relative' }}>
              <Typography variant="h6" gutterBottom color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AiIcon color="#9747FF" /> AI Updated
              </Typography>
              {loading ? (
                <Iconify icon="svg-spinners:blocks-shuffle-3" width={32} height={32} sx={{ color: 'primary.main', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
              ) : (
                <Editor
                  ref={editorRef}
                  value={decodeHTML(newContent?.ContentHtml ?? '')}
                  sx={editorStyle}
                  hideToolbar
                  editable={false}
                />
              )}
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="soft" onClick={onClose}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleApply}>Apply</Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default AiAssistantModal;
